import { useState, useRef, useMemo } from 'react';
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import {
  BasicTable,
  PageTopBar,
  PageView,
  CreateLocationModal,
  BulkEditLocationModal,
  Button
} from "../../components"
import { Loader } from "../../components/Loader"
import { fetchAllWarehouseLocations, fetchWarehouseLocationDetails } from "../../services/locationServices"
import { SelectColumnFilter } from "../../components/BasicTable/modules/ColumnFiltersUI"


export const ViewWarehouseLocationsContainer = () => {
  const { warehouse_id } = useParams();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [isCreateLocationModalOpen, setIsCreateLocationModalOpen] = useState(false);
  const [isBulkEditLocationModalOpen, setIsBulkEditLocationModalOpen] = useState(false);

  const buildFallbackCategoryCode = (code) => {
    if (!code) return "";
    return code.toLowerCase().replace(/_/g, " ").replace(/^\w/, c => c.toUpperCase());
  };
  const [searchParams, setSearchParams] = useState({
    page: 1,
    per_page: 50
  });
  const tableRef = useRef();

  const { isLoading, isError, error, data, isFetching, isPreviousData } = useQuery(
    ["all_warehouse_locations", searchParams, warehouse_id],
    () => fetchAllWarehouseLocations(warehouse_id, searchParams),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  // Fetch location types for filter options
  const { data: locationDetails } = useQuery(
    ['warehouse-location-details', warehouse_id],
    () => fetchWarehouseLocationDetails(warehouse_id),
    {
      enabled: !!warehouse_id,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  const onFilterChange = (activeFilters) => {
    columns.forEach((col) => {
      if (searchParams[col.accessor]) {
        delete searchParams[col.accessor]
      }
    })
    let filters = []
    activeFilters.forEach((filter) => {
      filters[filter.id] = filter.value
    })
    setSearchParams({ ...searchParams, ...filters, page: 1 })
  };

  const onSortChange = (orderBy) => {
    if (orderBy.length > 0) {
      setSearchParams({
        ...searchParams,
        order_by: orderBy[0].id,
        order: orderBy[0].desc ? "desc" : "asc",
      })
    } else {
      if ("order_by" in searchParams) {
        delete searchParams.order_by
        delete searchParams.order
        setSearchParams({ ...searchParams })
      }
    }
  }

  const columns = useMemo(() => {
    const locationTypeFilterOptions = locationDetails?.location_types?.map(type => ({
      id: type,
      label: type
    })) || [];

    console.log(locationTypeFilterOptions);

    const baseColumns = [
      {
        Header: t('location_modals.view_warehouse_locations.table.headers.location_type'),
        accessor: 'location_type',
        disableFilters: false,
        disableSortBy: true,
        Filter: SelectColumnFilter,
        selectFilterOptions: locationTypeFilterOptions,
      },
      {
        Header: t('location_modals.view_warehouse_locations.table.headers.volume_category'),
        accessor: 'volume_category',
        disableFilters: true,
        disableSortBy: true,
      },
      {
        Header: t('location_modals.view_warehouse_locations.table.headers.unique_code'),
        accessor: 'unique_code',
        disableFilters: false,
        disableSortBy: true,
      },
    ];

    // Only show Capacidad Máxima SKU column for warehouse_id 232 or 233
    if (warehouse_id === '232' || warehouse_id === '233') {
      baseColumns.push({
        Header: t('location_modals.view_warehouse_locations.table.headers.max_sku_capacity'),
        accessor: 'max_sku_capacity',
        disableFilters: true,
        disableSortBy: true,
      });
      baseColumns.push({
        Header: t('location_modals.view_warehouse_locations.table.headers.picking_priority'),
        accessor: 'picking_priority',
        disableFilters: true,
        disableSortBy: true,
      });
    }

    return baseColumns;
  }, [locationDetails, warehouse_id]);

  const getTableData = () => {
    if (!data?.warehouse_locations) return []
    return data.warehouse_locations.map((location) => {
      const baseData = {
        ...location,
        location_type: <div>{buildFallbackCategoryCode(location.location_type)}</div>,
        volume_category: <div>{buildFallbackCategoryCode(location.volume_category?.category_code) || 'N/A'}</div>,
        unique_code: <div className="font-mono">{location.unique_code}</div>,
      };

      // Only include max_sku_capacity and picking_priority for warehouse_id 232 or 233
      if (warehouse_id === '232' || warehouse_id === '233') {
        baseData.max_sku_capacity = <div>{location.max_sku_capacity || 'N/A'}</div>;
        baseData.picking_priority = <div>{location.picking_priority || 'N/A'}</div>;
      }

      return baseData;
    })
  };

  const handleLocationCreationSuccess = () => {
    // Invalidate and refetch the locations data
    queryClient.invalidateQueries(["all_warehouse_locations"]);
    queryClient.invalidateQueries(["warehouse-location-details"]);
  };

  const handleLocationEditSuccess = () => {
    // Invalidate and refetch the locations data
    queryClient.invalidateQueries(["all_warehouse_locations"]);
    queryClient.invalidateQueries(["warehouse-location-details"]);
  };

  return (
    <>
      <PageView
        topMenu={
          <PageTopBar>
            <div className="text-lg font-semibold">
              {t('common.inventory')}{" "}
              <span className="text-sm text-gray-400">
                {t('location_modals.view_warehouse_locations.title')} - 
              </span>
              <span className="text-sm text-gray-400">
                {t('location_modals.view_warehouse_locations.warehouse_prefix')} {warehouse_id}
              </span>
            </div>
            <div className="flex space-x-3">
              <Button
                type="primary"
                onClick={() => setIsCreateLocationModalOpen(true)}
              >
                {t('location_modals.view_warehouse_locations.buttons.create_locations')}
              </Button>
              <Button
                type="secondary"
                onClick={() => setIsBulkEditLocationModalOpen(true)}
              >
                {t('location_modals.view_warehouse_locations.buttons.edit_locations')}
              </Button>
            </div>
          </PageTopBar>
        }
      >
        {isError ? (
          <>Error: {error.message}</>
        ) : (
          <>
            {!isPreviousData && isLoading && <Loader show={true}></Loader>}
            <BasicTable
              onFilterChange={onFilterChange}
              onSortChange={onSortChange}
              columns={columns}
              showPaginator
              showHeader
              filterable
              sortable
              paginationMeta={data?.meta}
              labelToPaginator={t('location_modals.view_warehouse_locations.table.label')}
              pagesSize={searchParams.per_page}
              onPaginationChange={(requestedPage) => { setSearchParams({ ...searchParams, page: requestedPage }) }}
              onPageSizeChange={(pageSize) => {
                setSearchParams({ ...searchParams, per_page: pageSize, page: 1 })
                tableRef.current.resetPagination()
              }}
              showLoader={isFetching && isPreviousData}
              isFetching={isFetching}
              data={getTableData()}
              emptyTableText={t('location_modals.view_warehouse_locations.table.empty_text')}
              ref={tableRef}
            />
          </>
        )}
      </PageView>

      <CreateLocationModal
        open={isCreateLocationModalOpen}
        setOpen={setIsCreateLocationModalOpen}
        warehouseId={warehouse_id}
        onSuccess={handleLocationCreationSuccess}
      />

      <BulkEditLocationModal
        open={isBulkEditLocationModalOpen}
        setOpen={setIsBulkEditLocationModalOpen}
        warehouseId={warehouse_id}
        onSuccess={handleLocationEditSuccess}
      />
    </>
  );
};
